import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import syncRunner from "../lib/syncRunner";
import { downloadCatalog } from "../lib/catalogSync";
import { clearUserData, countPendingOutbox } from "../lib/offlineStore";
import {
  clearDevelopmentBearerToken,
  clearLegacyPersistentToken,
  setDevelopmentBearerToken,
} from "../lib/authSession";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = loading, false = unauthenticated, object = user
  const [user, setUser] = useState(null);
  const sessionGeneration = useRef(0);

  const startOfflineForUser = useCallback((u) => {
    if (!u || !u.id) return;
    syncRunner.start(u.id);
    // Catalog download is fire-and-forget so the UI never waits for it.
    downloadCatalog(u).catch(() => { /* tolerated */ });
  }, []);

  const loadMe = useCallback(async () => {
    const generation = ++sessionGeneration.current;
    try {
      const { data } = await api.get("/auth/me");
      if (generation !== sessionGeneration.current) return;
      setUser(data);
      startOfflineForUser(data);
    } catch (e) {
      if (generation !== sessionGeneration.current) return;
      // ONLY treat the token as invalid on a real 401 from the server.
      // A network error (no `response` field) means we're offline — keep
      // the token, hydrate the user from a cached snapshot if we have
      // one, and let the sync runner drain when connectivity returns.
      const status = e?.response?.status;
      if (status === 401) {
        syncRunner.stop();
        clearDevelopmentBearerToken();
        setUser(false);
        return;
      }
      // Offline boot path: try to recover the last-known user from
      // localStorage so the rest of the app (sync runner, catalog
      // lookups) has a userId. We only stash non-secret fields here.
      try {
        const cached = JSON.parse(localStorage.getItem("shelflife_user") || "null");
        if (cached && cached.id) {
          setUser(cached);
          startOfflineForUser(cached);
          return;
        }
      } catch (_) { /* ignore */ }
      // No cache to fall back to → treat as logged-out so the UI can
      // route to /login. The browser cookie remains server-managed; a
      // later online refresh can retry it without exposing it to JavaScript.
      syncRunner.stop();
      setUser(false);
    }
  }, [startOfflineForUser]);

  useEffect(() => {
    // Remove the historical persistent bearer token. Production HQ now uses
    // an HttpOnly cookie; loopback development uses sessionStorage only.
    clearLegacyPersistentToken();
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const generation = ++sessionGeneration.current;
    syncRunner.stop();
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (generation !== sessionGeneration.current) return { ok: false, error: "Session changed. Please sign in again." };
      setDevelopmentBearerToken(data.token);
      // Cache non-secret user fields so an offline reload can rehydrate
      // the session WITHOUT calling the server. Never include the token.
      try {
        localStorage.setItem("shelflife_user", JSON.stringify({
          id: data.user.id,
          email: data.user.email,
          role: data.user.role,
          name: data.user.name,
          department_id: data.user.department_id,
          department_name: data.user.department_name,
          store_id: data.user.store_id,
          store_name: data.user.store_name,
        }));
      } catch (_) { /* localStorage full? not fatal */ }
      setUser(data.user);
      startOfflineForUser(data.user);
      syncRunner.resume();
      return { ok: true, user: data.user };
    } catch (e) {
      if (generation !== sessionGeneration.current) return { ok: false, error: "Session changed. Please sign in again." };
      return { ok: false, error: formatApiErrorDetail(e) };
    }
  };

  /**
   * Logout, with confirmation when there are unsynced outbox entries.
   *
   * - `force` skips the confirmation prompt (caller already confirmed).
   * - Returns `{ ok: true }` on success or
   *   `{ ok: false, pending: N }` if the caller needs to confirm first.
   */
  const logout = async ({ force = false } = {}) => {
    const generation = ++sessionGeneration.current;
    const uid = user && user.id;
    if (uid && !force) {
      const pending = await countPendingOutbox(uid);
      if (generation !== sessionGeneration.current) return { ok: false, error: "Session changed." };
      if (pending > 0) {
        return { ok: false, pending };
      }
    }
    // Stop before the cookie/token transition, not after its response.
    syncRunner.stop();
    try { await api.post("/auth/logout"); } catch (_) { /* offline ok */ }
    if (generation !== sessionGeneration.current) return { ok: false, error: "Session changed." };
    // Tear down sync + local data BEFORE clearing the token so any
    // last-ditch listeners see a consistent state.
    if (uid) {
      try { await clearUserData(uid); } catch (_) { /* tolerated */ }
    }
    if (generation !== sessionGeneration.current) return { ok: false, error: "Session changed." };
    clearDevelopmentBearerToken();
    localStorage.removeItem("shelflife_user");
    setUser(false);
    return { ok: true };
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
